/**
 * Vervangt HubSpot-achtige {{tokens}} in platte tekst.
 * Gebruik `applyVariableMapDeep` voor heel `block.content` (geen JSON.stringify/parse).
 */

/** Waarden veilig als HTML-tekst (dangerouslySetInnerHTML). */
function escapeHtmlForVariableValue(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * TipTap variable nodes serialiseren naar
 * `<span class="variable-node" data-variable="{{token}}">Label</span>`.
 * Alleen `{{token}}` in de string vervangen raakt het label niet; deze stap vervangt de hele span.
 */
function resolveVariableNodeSpans(html: string, map: Record<string, string>): string {
  return html.replace(
    /<span\b([^>]+)>([^<]*)<\/span>/gi,
    (match, attrs: string) => {
      if (!/\bvariable-node\b/.test(attrs) || !/\bdata-variable="(\{\{[^}]+\}\})"/.test(attrs)) {
        return match;
      }
      const m = attrs.match(/\bdata-variable="(\{\{[^}]+\}\})"/);
      if (!m) return match;
      const token = m[1];
      return escapeHtmlForVariableValue(map[token] ?? '');
    },
  );
}

export function resolveVariablesFromMap(text: string, map: Record<string, string>): string {
  let resolved = resolveVariableNodeSpans(text, map);
  const entries = Object.entries(map).sort((a, b) => b[0].length - a[0].length);
  for (const [token, value] of entries) {
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    resolved = resolved.replace(new RegExp(escaped, 'g'), value ?? '');
  }
  return resolved;
}

/**
 * Recursief alle stringvelden in content vervangen (zelfde idee als backend public.ts applyDeep).
 */
export function applyVariableMapDeep<T>(val: T, map: Record<string, string>): T {
  if (!map || typeof map !== 'object' || Object.keys(map).length === 0) return val;

  function walk(v: unknown): unknown {
    if (typeof v === 'string') return resolveVariablesFromMap(v, map);
    if (Array.isArray(v)) return v.map(walk);
    if (v !== null && typeof v === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, x] of Object.entries(v)) {
        out[k] = walk(x);
      }
      return out;
    }
    return v;
  }

  return walk(val) as T;
}
